/**
 * Shared building blocks for NovaPet's printable clinical documents (receta, expediente,
 * órdenes de imagenología/laboratorio, …). Kept intentionally small and dependency-free —
 * every print module still builds its own full HTML string, this just avoids re-implementing
 * the same escaping/logo-caching/popup-printing plumbing (and the same clinic header/footer
 * chrome) in every new document type.
 *
 * NOTE for a future branding pass: `LOGO_PATH` + the footer's address/phones/social content
 * are still hardcoded (NovaPet is mono-clinic today). When that becomes Settings-driven,
 * this is the one file to touch instead of four — see `syncronization_totem/`.
 */

export type Translate = (key: string) => string;

export const LOGO_PATH = '/novapet_logo_ticket.png';
const logo_cache = new Map<string, Promise<string | null>>();

/** Escape for safe interpolation into print HTML (values are user-entered). */
export function esc(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/** Escaped value, or an em-dash placeholder when empty/nullish. */
export function dash(value: string | number | null | undefined): string {
  if (value == null) return '—';
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? esc(trimmed) : '—';
}

/** `<p class="row"><span class="lbl">Label:</span>value</p>` — the recurring label/value line. */
export function row(label: string, value: string | number | null | undefined): string {
  return `<p class="row"><span class="lbl">${esc(label)}:</span>${dash(value)}</p>`;
}

/** Fetches + caches a logo image as a data URI so the print popup never races a slow image load. */
export async function resolveLogoDataUri(path: string = LOGO_PATH): Promise<string | null> {
  const cached = logo_cache.get(path);
  if (cached) return cached;
  const promise = (async () => {
    try {
      const response = await fetch(path, { cache: 'force-cache' });
      if (!response.ok) return null;
      const blob = await response.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('logo_read_failed'));
        reader.onload = () => {
          if (typeof reader.result === 'string') resolve(reader.result);
          else reject(new Error('logo_read_failed'));
        };
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  })();
  logo_cache.set(path, promise);
  return promise;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function waitForPopupImages(popup: Window): Promise<void> {
  const images = Array.from(popup.document.images ?? []);
  if (images.length === 0) return;
  await Promise.race([
    Promise.all(
      images.map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              image.addEventListener('load', () => resolve(), { once: true });
              image.addEventListener('error', () => resolve(), { once: true });
            }),
      ),
    ).then(() => undefined),
    delay(1400),
  ]);
}

/**
 * Opens `html` in a new window and triggers the browser print dialog (user picks "Guardar
 * como PDF" as the destination — no PDF library anywhere in NovaPet). Returns false if the
 * popup was blocked so the caller can toast an explanation.
 */
export async function openPrintWindow(html: string): Promise<boolean> {
  const w = window.open('', '_blank', 'width=820,height=960');
  if (!w) return false;
  w.opener = null; // sever the same-origin back-reference (handle stays usable)
  w.document.open();
  w.document.write(html);
  w.document.close();
  await waitForPopupImages(w);
  w.focus();
  w.print();
  return true;
}

/** Shared CSS for every document's outer chrome (bordered sheet, section titles, boxes, grids). */
export const DOC_BASE_STYLES = `
  * { box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #17313a;
    margin: 0;
    padding: 10mm;
    background: #fff;
  }
  .page { page-break-after: always; }
  .page:last-child { page-break-after: auto; }
  .sheet {
    border: 2px solid #1f6d82;
    border-radius: 14px;
    padding: 14px 18px 18px;
  }
  .top-row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }
  .logo { height: 56px; width: auto; }
  .logo-fallback { font-size: 18px; font-weight: 800; color: #1f6d82; margin: 0; }
  .center-title { text-align: center; }
  .center-title .brand { font-size: 16px; font-weight: 800; letter-spacing: 0.5px; margin: 0; color: #17313a; }
  .center-title .tagline { font-size: 11px; font-weight: 700; margin: 1px 0; color: #1f6d82; text-transform: uppercase; }
  .center-title .doc-title { font-size: 12px; font-weight: 700; margin: 2px 0 0; text-transform: uppercase; letter-spacing: 0.4px; }
  .exp-box {
    border: 1px solid #1f6d82;
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    color: #1f6d82;
  }
  .tutor-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 12.5px;
    padding: 6px 0;
    border-bottom: 1px solid #1f6d82;
    margin-bottom: 8px;
  }
  .section-title {
    font-size: 12px;
    font-weight: 800;
    color: #1f6d82;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin: 10px 0 4px;
  }
  .resena-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 3px 18px;
    font-size: 12px;
  }
  .row { margin: 2px 0; }
  .lbl { color: #1f6d82; font-weight: 700; margin-right: 4px; }
  .box {
    border: 2px solid #1f6d82;
    border-radius: 12px;
    padding: 10px 16px 14px;
    margin-bottom: 14px;
    font-size: 12px;
  }
  .box.tall { min-height: 90px; }
  .box-title {
    text-align: center;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.4px;
    color: #1f6d82;
    background: #eaf4f6;
    margin: -10px -16px 10px;
    padding: 6px 16px;
    border-radius: 10px 10px 0 0;
    text-transform: uppercase;
  }
  .empty { color: #777; font-style: italic; margin: 0; }
  .signature-block {
    margin-top: 20px;
    padding-top: 8px;
    border-top: 1px solid #1f6d82;
    font-size: 12.5px;
    font-weight: 700;
  }
  @media print {
    body { padding: 6mm; }
    @page { margin: 6mm; }
  }
`;

/** Same footer every printable document that needs clinic contact info shares (receta). */
export const DOC_FOOTER_STYLES = `
  footer.doc-footer {
    position: relative;
    margin-top: 16px;
    text-align: center;
    font-size: 11px;
    color: #17313a;
    background: #dcedf0;
    border: 1px solid #1f6d82;
    border-radius: 12px;
    padding: 10px 44px 10px 16px;
  }
  footer.doc-footer p { margin: 3px 0; }
  footer.doc-footer .addr { font-weight: 700; }
  footer.doc-footer .phones {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-weight: 700;
    font-size: 13px;
  }
  footer.doc-footer .phones .rule {
    flex: 1;
    max-width: 70px;
    height: 0;
    border-top: 1px solid #1f6d82;
  }
  footer.doc-footer .social {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 10.5px;
    font-weight: 600;
  }
  footer.doc-footer .social .item { display: inline-flex; align-items: center; gap: 4px; }
  footer.doc-footer .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    border: 1px solid #1f6d82;
    font-size: 9px;
    line-height: 1;
    color: #1f6d82;
    flex: none;
  }
  .badge-24h {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: #6b4a3a;
    color: #fff;
    border: 3px solid #fff;
    box-shadow: 0 0 0 1px #1f6d82;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  .badge-24h .n { font-weight: 800; font-size: 13px; }
  .badge-24h .s { font-size: 5px; font-weight: 700; letter-spacing: 0.3px; margin-top: 1px; }
`;

/** Logo + centered brand/tagline/doc-title + a right-hand reference box (e.g. "No. Expediente"). */
export function buildDocHeaderHtml(args: {
  logo_data_uri: string | null;
  doc_title: string;
  corner_label: string;
  corner_value: string | number | null | undefined;
}): string {
  const logo_markup = args.logo_data_uri
    ? `<img class="logo" src="${esc(args.logo_data_uri)}" alt="Novapet" />`
    : '<p class="logo-fallback">NOVAPET</p>';
  return `
    <div class="top-row">
      <div class="logo-wrap">${logo_markup}</div>
      <div class="center-title">
        <p class="brand">NOVAPET</p>
        <p class="tagline">Hospital Veterinario de Especialidades</p>
        <p class="doc-title">${esc(args.doc_title)}</p>
      </div>
      <div class="exp-box">${esc(args.corner_label)}: ${dash(args.corner_value)}</div>
    </div>`;
}

/** The clinic contact footer shared with the prescription document. */
export function buildDocFooterHtml(): string {
  return `
    <footer class="doc-footer">
      <p class="addr">Prolongación Cuauhtémoc #224 Huizache 1 C.P. 34160 Durango, Dgo.</p>
      <p class="phones"><span class="rule"></span>618 185.57.25 / 618 112.38.96<span class="rule"></span></p>
      <p class="social">
        <span class="item"><span class="icon">&#9993;</span>contacto@novapet.mx</span>
        <span class="item"><span class="icon">&#127760;</span>www.novapet.mx</span>
        <span class="item"><span class="icon">f</span>NovapetMx</span>
      </p>
      <div class="badge-24h"><span class="n">24</span><span class="s">URGENCIAS</span></div>
    </footer>`;
}
